import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
    ShieldCheck, LogOut, Bell, Menu, LayoutDashboard, Users, Building2, Wrench, Video,
    Handshake, CreditCard, ScanLine, MessageSquare, AlertTriangle, LifeBuoy,
    BarChart3, Settings, ArrowRight, Presentation as PresentationIcon,
} from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import { api } from "../lib/api";

/* =============================================================
   GPM ADMIN CONSOLE — mobile-first responsive layout.
   Only shown when the logged-in user has role='admin'.
   Reuses existing admin routes (no new admin system).
   ============================================================= */

// Tap tile — icon + label + optional live count badge.
function AdminTile({ icon: Icon, label, sub, to, tint, count, testid }) {
    return (
        <Link
            to={to}
            data-testid={testid}
            className="relative rounded-2xl p-3 border flex flex-col items-start justify-between min-h-[112px] active:scale-[0.97] transition-transform"
            style={{ backgroundColor: tint.bg, borderColor: "rgba(0,38,87,0.08)" }}
        >
            <div className="flex items-start justify-between w-full">
                <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ backgroundColor: tint.iconBg }}>
                    <Icon className="w-5 h-5" style={{ color: tint.fg }} strokeWidth={2.2} />
                </div>
                {count !== undefined && count !== null && (
                    <span className="min-w-[26px] h-6 px-2 rounded-full flex items-center justify-center font-extrabold text-[11px]" style={{ backgroundColor: "#DAA520", color: "#1F1300" }}>{count}</span>
                )}
            </div>
            <div className="mt-2">
                <div className="font-extrabold text-[12px] leading-tight" style={{ color: "#002657" }}>{label}</div>
                {sub && <div className="text-[10px] font-semibold mt-0.5" style={{ color: "rgba(0,38,87,0.55)" }}>{sub}</div>}
            </div>
        </Link>
    );
}

function Section({ title, testid, children }) {
    return (
        <section className="mt-4 mx-3" data-testid={testid}>
            <div className="rounded-2xl bg-white p-3 sm:p-4 shadow-sm border" style={{ borderColor: "rgba(0,38,87,0.06)" }}>
                <div className="text-[10px] uppercase tracking-[0.18em] font-extrabold px-1 mb-3" style={{ color: "#002657" }}>{title}</div>
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2">{children}</div>
            </div>
        </section>
    );
}

const T = {
    green:   { bg: "#DCFCE7", iconBg: "#BBF7D0", fg: "#16A34A" },
    blue:    { bg: "#DBEAFE", iconBg: "#BFDBFE", fg: "#2563EB" },
    amber:   { bg: "#FEF3C7", iconBg: "#FDE68A", fg: "#D97706" },
    rose:    { bg: "#FFE4E6", iconBg: "#FECDD3", fg: "#E11D48" },
    purple:  { bg: "#EDE9FE", iconBg: "#DDD6FE", fg: "#7C3AED" },
    indigo:  { bg: "#E0E7FF", iconBg: "#C7D2FE", fg: "#4F46E5" },
    peach:   { bg: "#FED7AA", iconBg: "#FDBA74", fg: "#EA580C" },
    emerald: { bg: "#D1FAE5", iconBg: "#A7F3D0", fg: "#059669" },
    slate:   { bg: "#F1F5F9", iconBg: "#E2E8F0", fg: "#475569" },
    gold:    { bg: "#FEF3C7", iconBg: "#FDE68A", fg: "#B45309" },
};

export default function AdminMobileConsole() {
    const { user, logout } = useAuth();
    const nav = useNavigate();
    const [counts, setCounts] = useState({ users: null, properties: null, partners: null, kyc: null, complaints: null, inquiries: null });

    // Load live dashboard counts from existing admin endpoints (safe if any fail).
    useEffect(() => {
        (async () => {
            const [users, props, partners, kyc, complaints, inquiries] = await Promise.all([
                api.get("/admin/users", { params: { limit: 1 } }).then((r) => r.data?.total ?? r.data?.items?.length ?? 0).catch(() => null),
                api.get("/admin/properties", { params: { limit: 1 } }).then((r) => r.data?.total ?? r.data?.items?.length ?? 0).catch(() => null),
                api.get("/admin/partners", { params: { limit: 1 } }).then((r) => r.data?.stats?.active ?? r.data?.total ?? 0).catch(() => null),
                api.get("/admin/kyc/pending").then((r) => r.data?.items?.length ?? 0).catch(() => null),
                api.get("/admin/complaints", { params: { status: "open" } }).then((r) => r.data?.items?.length ?? 0).catch(() => null),
                api.get("/admin/inquiries", { params: { limit: 1 } }).then((r) => r.data?.total ?? r.data?.items?.length ?? 0).catch(() => null),
            ]);
            setCounts({ users, properties: props, partners, kyc, complaints, inquiries });
        })();
    }, []);

    if (!user) return null;

    const onLogout = () => { logout(); toast.success("Logged out"); nav("/"); };

    return (
        <div className="min-h-screen bg-[#f6f7fb] pb-8" data-testid="admin-console-mobile">
            {/* ============ HEADER ============ */}
            <div
                className="text-on-image px-4 pt-3 pb-5 relative overflow-hidden"
                style={{ background: "linear-gradient(180deg, #0A1229 0%, #002657 100%)" }}
                data-testid="admin-console-hero"
            >
                <div aria-hidden className="absolute inset-0 pointer-events-none opacity-30" style={{ background: "radial-gradient(ellipse 420px 220px at 100% 0%, rgba(218,165,32,0.45), transparent 60%)" }} />

                <div className="relative flex items-center justify-between">
                    <div>
                        <div className="text-[10px] uppercase tracking-[0.22em] font-extrabold" style={{ color: "#F5E5A5" }}>GPM Admin Console</div>
                        <h1 className="font-heading font-extrabold text-white text-xl mt-0.5">Control Center</h1>
                    </div>
                    <div className="flex items-center gap-2">
                        <Link to="/notifications" data-testid="admin-notifs" aria-label="Notifications" className="w-9 h-9 rounded-full bg-white/12 backdrop-blur-md border border-white/20 flex items-center justify-center relative">
                            <Bell className="w-4 h-4 text-white" />
                            <span className="absolute top-1.5 right-1.5 w-1.5 h-1.5 rounded-full bg-rose-500" />
                        </Link>
                        <Link to="/dashboard" data-testid="admin-full-console" aria-label="Full desktop console" className="w-9 h-9 rounded-full bg-white/12 backdrop-blur-md border border-white/20 flex items-center justify-center">
                            <Menu className="w-4 h-4 text-white" />
                        </Link>
                    </div>
                </div>

                {/* Admin identity strip */}
                <div className="relative mt-4 flex items-center gap-3">
                    <div className="w-12 h-12 rounded-full flex items-center justify-center font-heading font-extrabold shadow" style={{ background: "linear-gradient(135deg, #DAA520, #F59E0B)", color: "#001a3d" }}>
                        <ShieldCheck className="w-6 h-6" />
                    </div>
                    <div className="min-w-0">
                        <div className="text-white font-extrabold text-sm truncate" data-testid="admin-name">{user.name || "Admin"}</div>
                        <div className="text-[11px] truncate" style={{ color: "#F5E5A5" }} data-testid="admin-email">{user.email} · Administrator</div>
                    </div>
                </div>
            </div>

            {/* ============ ADMIN OVERVIEW (live counts) ============ */}
            <Section title="Admin Overview" testid="admin-section-overview">
                <AdminTile icon={LayoutDashboard} label="Full Console" sub="Desktop dashboard" to="/dashboard" tint={T.slate} testid="admin-full-console-tile" />
                <AdminTile icon={Users} label="Users" count={counts.users} to="/dashboard?tab=users" tint={T.blue} testid="admin-users" />
                <AdminTile icon={Building2} label="Properties" count={counts.properties} to="/dashboard?tab=properties" tint={T.green} testid="admin-properties" />
                <AdminTile icon={Handshake} label="GPM Partners" count={counts.partners} to="/dashboard?tab=gpm_partners" tint={T.gold} testid="admin-partners" />
            </Section>

            {/* ============ CATALOGUE MANAGEMENT ============ */}
            <Section title="Catalogue Management" testid="admin-section-catalogue">
                <AdminTile icon={Building2} label="Properties" sub="Approve / hide" to="/dashboard?tab=properties" tint={T.green} testid="admin-mgmt-properties" />
                <AdminTile icon={Wrench} label="Services" sub="Portfolios & bookings" to="/dashboard?tab=services" tint={T.amber} testid="admin-mgmt-services" />
                <AdminTile icon={Video} label="Reels" sub="Moderation" to="/dashboard?tab=reels" tint={T.rose} testid="admin-mgmt-reels" />
                <AdminTile icon={PresentationIcon} label="Content" sub="Refer deck & settings" to="/dashboard?tab=settings" tint={T.purple} testid="admin-mgmt-content" />
            </Section>

            {/* ============ FINANCE & KYC ============ */}
            <Section title="Finance & Verification" testid="admin-section-finance">
                <AdminTile icon={CreditCard} label="Payments" sub="Orders & payouts" to="/dashboard?tab=payments" tint={T.blue} testid="admin-payments" />
                <AdminTile icon={ScanLine} label="KYC Review" count={counts.kyc} to="/dashboard?tab=kyc" tint={T.emerald} testid="admin-kyc" />
                <AdminTile icon={Handshake} label="Referrals & Commission" to="/dashboard?tab=referrals" tint={T.gold} testid="admin-referrals" />
                <AdminTile icon={MessageSquare} label="Enquiries & Leads" count={counts.inquiries} to="/dashboard?tab=inquiries" tint={T.indigo} testid="admin-inquiries" />
            </Section>

            {/* ============ OPERATIONS ============ */}
            <Section title="Operations" testid="admin-section-ops">
                <AdminTile icon={AlertTriangle} label="Reports & Complaints" count={counts.complaints} to="/dashboard?tab=complaints" tint={T.peach} testid="admin-complaints" />
                <AdminTile icon={LifeBuoy} label="Support Messages" to="/dashboard?tab=support" tint={T.rose} testid="admin-support" />
                <AdminTile icon={Bell} label="Notifications" sub="Broadcast & templates" to="/dashboard?tab=notifications" tint={T.indigo} testid="admin-notifications" />
                <AdminTile icon={BarChart3} label="Analytics" sub="KPIs & reports" to="/dashboard?tab=analytics" tint={T.purple} testid="admin-analytics" />
            </Section>

            {/* ============ SITE SETTINGS ============ */}
            <Section title="Site Settings" testid="admin-section-settings">
                <AdminTile icon={Settings} label="Website Content" sub="Homepage & CMS" to="/dashboard?tab=settings" tint={T.slate} testid="admin-site-settings" />
                <AdminTile icon={ShieldCheck} label="Admin Users" sub="Roles & access" to="/dashboard?tab=admin_users" tint={T.blue} testid="admin-users-roles" />
            </Section>

            {/* Hint + Logout */}
            <div className="mx-3 mt-4">
                <Link to="/dashboard" data-testid="admin-open-desktop" className="w-full inline-flex items-center justify-center gap-2 py-3 rounded-2xl font-bold text-[13px] shadow-sm" style={{ background: "linear-gradient(90deg, #DAA520, #F59E0B)", color: "#001a3d" }}>
                    Open Full Desktop Console <ArrowRight className="w-4 h-4" />
                </Link>
            </div>
            <div className="mx-3 mt-3">
                <button type="button" onClick={onLogout} data-testid="admin-logout" className="w-full inline-flex items-center justify-center gap-2 py-3 rounded-2xl bg-white border font-bold text-[13px]" style={{ borderColor: "rgba(220,38,38,0.2)", color: "#DC2626" }}>
                    <LogOut className="w-4 h-4" /> Log out
                </button>
            </div>

            <div className="text-center py-5 text-[10px] uppercase tracking-widest" style={{ color: "rgba(0,38,87,0.4)" }}>
                Global Property Mart · Admin Console
            </div>
        </div>
    );
}
